/**
 * Appendix A.4 — Entity schema (sim state).
 * Plain serializable data: no class instances, no functions, no cyclic refs.
 * Iteration order everywhere is ascending pool_index.
 */

export interface Vec2 {
  x: number;
  y: number;
}

export type EntityKind = 'player' | 'enemy' | 'projectile' | 'pickup' | 'hazard';
export type Faction = 'player' | 'ally' | 'enemy' | 'neutral';

export interface StatusInstance {
  status_id: string;
  stacks: number;
  remaining_s: number;
  tick_rate_hz: number;
  next_tick: number; // absolute sim tick
  snapshot: { magnitude_per_stack: number };
  source_id: number;
}

export const MAX_STATUS_SLOTS = 8;

export interface EntityBase {
  id: number; // uint32, monotonic, spawn order
  kind: EntityKind;
  pos: Vec2;
  vel: Vec2;
  hp: number;
  max_hp: number;
  hitbox_radius: number;
  faction: Faction;
  status_effects: StatusInstance[];
  status_count: number; // live entries in status_effects (slots are preallocated)
  alive: boolean;
  spawn_tick: number;
  targetable: boolean;
  pool_index: number;
}

export interface WeaponSlot {
  def_id: string;
  level: number;
  evolved: boolean;
  cooldown_until: number;
  last_target_id: number;
  policy: string;
  slot: number;
  /** Rotational state for orbital/beam weapons; presentation reads, sim owns. */
  phase: number;
}

export interface PassiveSlot {
  def_id: string;
  rank: number;
}

export interface Player extends EntityBase {
  kind: 'player';
  lumensurge_tier: number; // 1–10
  surge_progress: number; // motes toward next tier
  weapons: WeaponSlot[]; // ≤ 4
  weapon_count: number;
  passives: PassiveSlot[]; // ≤ 4
  passive_count: number;
  active: { def_id: string; cooldown_until: number } | null;
  dash: {
    active: boolean;
    dir: Vec2;
    iframes_until: number;
    cooldown_until: number;
    end_tick: number;
  };
  mercy_until: number;
  focus_aim: { held: boolean; dir: Vec2 };
  rerolls_used: number;
  level: number;
  xp: number;
  xp_to_next: number;
  /** Aggregate stat multipliers recomputed on any build change (§4.1). */
  stats: PlayerStats;
  focus_slot: number;
}

export interface PlayerStats {
  damage_mult: number;
  area_mult: number;
  speed_mult: number; // projectile speed
  duration_mult: number;
  cooldown_mult: number; // < 1 is faster
  count_add: number;
  crit_chance: number;
  crit_mult: number;
  move_speed_mult: number;
  pickup_radius_mult: number;
  max_hp_add: number;
  armor: number; // flat reduction, applied per §4
  regen_per_s: number;
  xp_gain_mult: number;
  luck: number;
  knockback_mult: number;
}

export function defaultPlayerStats(): PlayerStats {
  return {
    damage_mult: 1,
    area_mult: 1,
    speed_mult: 1,
    duration_mult: 1,
    cooldown_mult: 1,
    count_add: 0,
    crit_chance: 0.05,
    crit_mult: 1.5,
    move_speed_mult: 1,
    pickup_radius_mult: 1,
    max_hp_add: 0,
    armor: 0,
    regen_per_s: 0,
    xp_gain_mult: 1,
    luck: 0,
    knockback_mult: 1,
  };
}

export interface Enemy extends EntityBase {
  kind: 'enemy';
  def_id: string;
  elite: boolean;
  affixes: string[];
  affix_count: number;
  threat_score: number;
  steering: {
    formation_slot: number;
    goal: Vec2;
    /** Persistent heading used for turn-rate limiting. */
    heading: number;
    wander: number;
  };
  age_ticks: number;
  telegraph_until: number;
  /** Attack scheduling for ranged/charge archetypes. */
  attack_ready_tick: number;
  attack_state: number; // 0 idle, 1 winding, 2 committed, 3 recovering
  attack_dir: Vec2;
  /** Shield pool for affix.shielded. */
  shield: number;
  shield_regen_at: number;
  stagger_until: number;
  contact_cd_until: number;
  /** Knockback velocity, decays separately from steering (§03.7). */
  knock: Vec2;
  boss_phase_index: number;
  boss_enrage_tick: number;
  is_boss: boolean;
  scale: number;
  /** Cached for render/targeting; recomputed on damage. */
  hp_frac: number;
  spawn_anim: number; // 0..1 birth progress
  death_anim: number; // 0..1 death progress; > 0 means dying
}

export interface Projectile extends EntityBase {
  kind: 'projectile';
  weapon_id: string;
  owner_id: number;
  damage_snapshot: number;
  crit_snapshot: boolean;
  area_snapshot: number;
  knockback_snapshot: number;
  penetration_left: number;
  ricochet_left: number;
  expire_tick: number;
  /** Per-enemy hit cooldown set for orbital/beam/persistent shapes. */
  hit_ids: number[];
  hit_count: number;
  /** Behaviour selector resolved from weapon class. */
  behaviour: number;
  /** Orbit/beam parameters. */
  phase: number;
  orbit_radius: number;
  homing_strength: number;
  target_id: number;
  status_on_hit: string;
  visual_class: number;
  spawn_tick_f: number;
}

export type PickupKind = 'xp_mote' | 'health' | 'powerup';

export interface Pickup extends EntityBase {
  kind: 'pickup';
  pickup_kind: PickupKind;
  value: number;
  magnet: boolean;
  /** 0/1/2 rings = 1/5/25 denomination double-code (spine §12). */
  rings: number;
  powerup_id: string;
  bob_phase: number;
}

export interface Hazard extends EntityBase {
  kind: 'hazard';
  hazard_id: string;
  zone: { shape: 'circle' | 'rect'; r: number; w: number; h: number };
  arm_tick: number;
  end_tick: number;
  telegraph_until: number;
  damage_per_s: number;
  status_id: string;
  owner_id: number;
  rot: number;
}

// --- factories: called once at pool construction, never in the hot loop ---

function baseFields(kind: EntityKind, index: number): EntityBase {
  const status: StatusInstance[] = new Array(MAX_STATUS_SLOTS);
  for (let i = 0; i < MAX_STATUS_SLOTS; i++) {
    status[i] = {
      status_id: '',
      stacks: 0,
      remaining_s: 0,
      tick_rate_hz: 0,
      next_tick: 0,
      snapshot: { magnitude_per_stack: 0 },
      source_id: 0,
    };
  }
  return {
    id: 0,
    kind,
    pos: { x: 0, y: 0 },
    vel: { x: 0, y: 0 },
    hp: 0,
    max_hp: 0,
    hitbox_radius: 0.5,
    faction: 'neutral',
    status_effects: status,
    status_count: 0,
    alive: false,
    spawn_tick: 0,
    targetable: false,
    pool_index: index,
  };
}

export function createEnemy(index: number): Enemy {
  return {
    ...baseFields('enemy', index),
    kind: 'enemy',
    def_id: '',
    elite: false,
    affixes: ['', ''],
    affix_count: 0,
    threat_score: 0,
    steering: { formation_slot: 0, goal: { x: 0, y: 0 }, heading: 0, wander: 0 },
    age_ticks: 0,
    telegraph_until: 0,
    attack_ready_tick: 0,
    attack_state: 0,
    attack_dir: { x: 0, y: 0 },
    shield: 0,
    shield_regen_at: 0,
    stagger_until: 0,
    contact_cd_until: 0,
    knock: { x: 0, y: 0 },
    boss_phase_index: 0,
    boss_enrage_tick: 0,
    is_boss: false,
    scale: 1,
    hp_frac: 1,
    spawn_anim: 0,
    death_anim: 0,
  };
}

export function createProjectile(index: number): Projectile {
  const hit: number[] = new Array(16);
  hit.fill(0);
  return {
    ...baseFields('projectile', index),
    kind: 'projectile',
    weapon_id: '',
    owner_id: 0,
    damage_snapshot: 0,
    crit_snapshot: false,
    area_snapshot: 0,
    knockback_snapshot: 0,
    penetration_left: 0,
    ricochet_left: 0,
    expire_tick: 0,
    hit_ids: hit,
    hit_count: 0,
    behaviour: 0,
    phase: 0,
    orbit_radius: 0,
    homing_strength: 0,
    target_id: 0,
    status_on_hit: '',
    visual_class: 0,
    spawn_tick_f: 0,
  };
}

export function createPickup(index: number): Pickup {
  return {
    ...baseFields('pickup', index),
    kind: 'pickup',
    pickup_kind: 'xp_mote',
    value: 1,
    magnet: false,
    rings: 0,
    powerup_id: '',
    bob_phase: 0,
  };
}

export function createHazard(index: number): Hazard {
  return {
    ...baseFields('hazard', index),
    kind: 'hazard',
    hazard_id: '',
    zone: { shape: 'circle', r: 0, w: 0, h: 0 },
    arm_tick: 0,
    end_tick: 0,
    telegraph_until: 0,
    damage_per_s: 0,
    status_id: '',
    owner_id: 0,
    rot: 0,
  };
}

export function createPlayer(): Player {
  const weapons: WeaponSlot[] = [];
  for (let i = 0; i < 4; i++) {
    weapons.push({ def_id: '', level: 0, evolved: false, cooldown_until: 0, last_target_id: 0, policy: 'nearest', slot: i, phase: 0 });
  }
  const passives: PassiveSlot[] = [];
  for (let i = 0; i < 4; i++) passives.push({ def_id: '', rank: 0 });
  return {
    ...baseFields('player', 0),
    kind: 'player',
    faction: 'player',
    lumensurge_tier: 1,
    surge_progress: 0,
    weapons,
    weapon_count: 0,
    passives,
    passive_count: 0,
    active: { def_id: 'active.dash', cooldown_until: 0 },
    dash: { active: false, dir: { x: 0, y: 0 }, iframes_until: 0, cooldown_until: 0, end_tick: 0 },
    mercy_until: 0,
    focus_aim: { held: false, dir: { x: 1, y: 0 } },
    rerolls_used: 0,
    level: 1,
    xp: 0,
    xp_to_next: 5,
    stats: defaultPlayerStats(),
    focus_slot: 0,
    alive: true,
    targetable: true,
  };
}
